import { useContext, useEffect, useState } from 'react'
import { Box, Checkbox, Container, Divider, Grid, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

import PaymentSummarySkeleton from '../../Membership/PaymentSummary/PaymentSummary.skeleton'
import CommonBackButton from '../../../atoms/CommonBackButton/CommonBackButton'
import { Route } from '../../../../router'
import { AppContext } from '../../../../stores/App/AppStore'
import { UserContext } from '../../../../stores/User/UserStore'
import { DatePatterns } from '../../../../types/date-patterns'
import { StripePaymentModal } from '../../../molecules/StripeModal/StripePaymentModal'
import ConfirmationDialog from '../../../molecules/ConfirmationDialog/ConfirmationDialog'
import TermsAndConditionsCheckbox from '../../../organisms/TermsAndConditionsCheckbox/TermsAndConditionsCheckbox'
import { logLandingPageAction } from '../../../../clients/logger'
import { errorPopupMessageTemplate, generateErrorCode, subscriptionGiftPaymentMessageTemplate } from '../../../../helpers/logging/templates'
import useTrackPageView from "../../../../helpers/hooks/use-track-page-view"

import styles from './SubscriptionGiftPayment.module.css'
import { useMembershipSettingsRepository } from '../../../../repositories/MembershipSettings/use-membership-settings-repository'
import { useSubscriptionGiftRepository } from '../../../../repositories/SubscriptionGift/use-subscription-gift-repository'
import SubscriptionGiftPaymentSummaryDetails from '../../../atoms/SubscriptionGiftPaymentSummaryDetails/SubscriptionGiftPaymentSummaryDetails'
import PaymentSummaryDetailsTotal from '../../../atoms/PaymentSummaryDetailsTotal/PaymentSummaryDetailsTotal'
import SubscriptionGiftStepperStep from '../../../molecules/SubscriptionGiftStepper/SubscriptionGiftStepper'

const DECIMALS = 2

const SubscriptionGiftSenderPayment = () => {
    useTrackPageView(`Subscription Gift Sender Payment Page`)

    const { t } = useTranslation()

    const navigate = useNavigate()
    const { updateGiftAndStripePaymentOrderOnSuccess } = useSubscriptionGiftRepository()

    const { state: appState } = useContext(AppContext)
    const { state: userState } = useContext(UserContext)

    const [formStarterTermsConsent, setFormStarterTermsConsent] = useState(false)
    const [subscriptionGiftAcknowledgement, setSubscriptionGiftAcknowledgement] = useState(false)
    const [dialogErrorMessageTitle, setDialogErrorMessageTitle] = useState(t(`sign_up_failure`))
    const [dialogErrorMessage, setDialogErrorMessage] = useState(``)
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false)
    const { retrieveMembershipSettings } = useMembershipSettingsRepository()

    const userEmail = userState.subscriptionGift?.myEmail

    useEffect(() => {
        logLandingPageAction(errorPopupMessageTemplate(dialogErrorMessage + `Subscription Gift Payment page with senderEmail:` + userState.subscriptionGift?.myEmail), `myInfo`)
    }, [dialogErrorMessage])

    useEffect(() => {
        if (!userState.subscriptionGift?.myEmail
            || !userState.subscriptionGift?.dateOfBirth
            || !userState.subscriptionGift?.subscriptionPeriod
            || !userState.subscriptionGift?.code
        ) {
            navigate(Route.SUBSCRIPTION_GIFT_LANDING)
        }
        if (!userState.membershipSettings?.data){
            retrieveMembershipSettings()
        }
    }, [])

    const handleOnSuccess = () => {
        logLandingPageAction(subscriptionGiftPaymentMessageTemplate(paymentSummaryAmount, userState.subscriptionGift?.myEmail, userState.subscriptionGift?.recipientName), `myInfo`)
        updateGiftAndStripePaymentOrderOnSuccess(userState.subscriptionGift?.code ?? ``)
        navigate(Route.SUBSCRIPTION_GIFT_PAYMENT_SUCCESS)
    }

    const showConsentDialog = () => {
        setDialogErrorMessageTitle(t(`sign_up_failure_terms_and_conditions`))
        setDialogErrorMessage(t(`sign_up_terms_and_conditions`) + ` error code: ${generateErrorCode()}`)
        setIsErrorDialogOpen(true)
    }

    const paymentAmountFormatter = (): string => {
        let paymentAmount
        if (userState.subscriptionGift?.membershipPlanKey && userState.membershipPlan?.data?.membershipFees) {
            // eslint-disable-next-line no-magic-numbers
            paymentAmount = (((userState.subscriptionGift?.subscriptionPeriod ?? 1) - 1) * (userState.membershipSettings?.data?.membershipFees ?? 36)) + userState.membershipPlan?.data?.membershipFees
        }else {
            // eslint-disable-next-line no-magic-numbers
            paymentAmount = (userState.subscriptionGift?.subscriptionPeriod ?? 1) * (userState.membershipSettings?.data?.membershipFees ?? 36)
        }
        return paymentAmount.toFixed(DECIMALS)
    }

    if (!appState.giftPaymentClientSecret) {
        return <PaymentSummarySkeleton />
    }

    const paymentSummaryAmount = paymentAmountFormatter()
    const startDate = moment().format(DatePatterns.DATE)
    const endDate = moment().add(userState.subscriptionGift?.subscriptionPeriod, `years`).subtract(1, `month`).endOf(`month`).format(DatePatterns.DATE)
    const stripeClientSecret = appState.giftPaymentClientSecret

    return (
        <Container>
            <CommonBackButton />
            <SubscriptionGiftStepperStep activeStep={3}/>
            <Grid container className={styles.gridContainer}>

                <Grid className={styles.paymentSummaryHeading}>
                    <Typography variant={`h3`} mb={1}>{t(`payment_summary_subscription_gift_make_payment_header`)}</Typography>
                    <Typography variant={`h6`}>{t(`payment_summary_subscription_gift_make_payment_subtitle`)}</Typography>
                </Grid>

                <Grid className={styles.paymentSummaryDetails}>
                    <Box className={styles.paymentSummaryDetailsBox}>
                        <Typography variant={`h6`} mb={2}>{t(`payment_summary_subscription_gift_setup_details`)}</Typography>
                        <SubscriptionGiftPaymentSummaryDetails isSetup={false} startDate={startDate} endDate={endDate} paymentSummaryAmount={paymentSummaryAmount} subscriptionPeriod={userState.subscriptionGift?.subscriptionPeriod ?? 1}/>

                        <Divider className={styles.paymentSummaryDetailsDivider} />

                        <PaymentSummaryDetailsTotal paymentSummaryAmount={paymentSummaryAmount}/>

                        <Grid container direction={`row`} justifyContent={`space-between`} alignItems={`start`} paddingTop={4} spacing={2}>
                            <Grid item>
                                <Typography variant={`body2`}>{t(`subscription_gift_payment_summary_recurring_payment_please_note`)}</Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant={`body2`}>{t(`subscription_gift_payment_summary_recurring_payment_notice`)}</Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant={`body2`}>{t(`subscription_gift_payment_summary_automatic_cancellation`, { cancelAt: endDate })}</Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Grid container paddingTop={4} direction={`row`}>
                        <TermsAndConditionsCheckbox starterTemsConsent={formStarterTermsConsent} onTermsConsentChange={(starterTermsConsent) => setFormStarterTermsConsent(starterTermsConsent)} />
                    </Grid>

                    <Grid container paddingTop={4} direction={`row`}>
                        <Container className={styles.termsContainer}>
                            <Checkbox checked={subscriptionGiftAcknowledgement} onChange={() => setSubscriptionGiftAcknowledgement(!subscriptionGiftAcknowledgement)} color='functional' />
                            <Typography variant="body1" className={styles.termsText}>
                                {t(`subscription_gift_acknowledgement`)}
                            </Typography>
                        </Container>
                    </Grid>
                    <Typography variant="h6">
                        {t(`payment_summary_setup_payment_method_title`)}
                    </Typography>
                    <Grid container paddingTop={4} direction={`row`}>
                        <StripePaymentModal onConsentFalse={showConsentDialog} consent={formStarterTermsConsent && subscriptionGiftAcknowledgement} clientSecret={stripeClientSecret} email={userEmail} isFirstYearFree={false} isSubscriptionGiftPurchase={true} isSubscriptionGiftRedemption={false} isBundlePurchase={false} onSuccess={handleOnSuccess} />
                    </Grid>
                </Grid>
            </Grid>

            <ConfirmationDialog
                buttonText={t(`common_button_confirmation`)}
                confirmationTitle={dialogErrorMessageTitle}
                confirmationText={dialogErrorMessage}
                dismissButtonText={t(`common_back_to_home`)}
                defaultOpen={isErrorDialogOpen}
                onConfirm={() => {setIsErrorDialogOpen(false)}}
                onDismiss={() => navigate(Route.ROOT)}
            />
        </Container>
    )
}

export default SubscriptionGiftSenderPayment
